import { useQuery } from "@tanstack/react-query";
import { get_providers } from "../api/api-methods";
import { useSelector, useDispatch } from "react-redux";
import { setProviders } from "../redux-store/features/providerSlice";
import { useMemo } from "react";

export const useProviderData = (
  currentPage,
  selectedIds,
  rfiIds,
  providerTypes,
  indications,
  companySizeFiltersId,
  selectedRevenueRange,
  revenueRangeString,
  reportAvailable,
  dataAvailability,
  countries, states, cities,
  searchText
) => {
  const dispatch = useDispatch();
  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage["discovery"]
    ) || "";

  const includeReportAvailable =
    reportAvailable?.is_rfi_available === true ||
    reportAvailable?.is_audit_report_available === true;

  const includeRangeRevenue =
    selectedRevenueRange?.min != null && selectedRevenueRange?.max != null;

  const payload = useMemo(
    () => ({
      page_size: 21,
      current_page: currentPage,
      ...(selectedIds?.length && { provider_id_list: selectedIds }),
      ...(rfiIds?.length && { rfi_template_id_list: rfiIds }),
      ...(providerTypes?.length && { provider_type_id_list: providerTypes }),
      ...(indications?.length && { indication_id_list: indications }),
      ...(revenueRangeString?.length && {
        revenue_range_string: revenueRangeString,
      }),
      ...(companySizeFiltersId?.length && {
        employee_count_range_list: companySizeFiltersId,
      }),
      ...(includeRangeRevenue && {
        revenue_range: selectedRevenueRange,
      }),
      ...(includeReportAvailable && { report_availability: reportAvailable }),
      ...(dataAvailability?.length && { data_availability: dataAvailability }),
      ...(countries?.length && { country_list: countries.map(c => c === 'US' ? 'USA' : c) }),
      ...(states?.length && { state_list: states }),
      ...(cities?.length && { city_list: cities }),
      search_text: (searchQuery != null && searchQuery !== "") ? encodeURIComponent(searchQuery) : "",
      discovery_status: true,
    }),
    [
      currentPage,
      selectedIds,
      rfiIds,
      providerTypes,
      indications,
      companySizeFiltersId,
      selectedRevenueRange,
      revenueRangeString,
      includeRangeRevenue,
      reportAvailable,
      dataAvailability,
      includeReportAvailable,
      countries,
      states,
      cities,
      searchQuery,
    ]
  );

  // Fetch providers function
  const fetchProviders = async () => {
    const response = await get_providers.getAllProvider(payload, {
      headers: { "Content-Type": "application/json" },
    });

    const items = [...(response.items || [])];
    // Prioritize providers with logo over letter-only placeholders
    items.sort((a, b) => (!!b.logo ? 1 : 0) - (!!a.logo ? 1 : 0));

    dispatch(setProviders(items));
    return { ...response, items };
  };

  // UseQuery hook to fetch providers based on the payload
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: [
      "search-providers",
      payload, // Use payload directly for the queryKey
    ],
    queryFn: fetchProviders,
    enabled: !!currentPage, // Run search with or without filters; backend is null-safe and search_text is sent as "" when empty
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 1,
  });

  return {
    providers: data?.items || [],
    totalPages: data?.total_pages || 0,
    isLoading,
    isError: error?.message,
    totalItems: data?.total_items,
    refetch,
  };
};
